/**
 * PL Home Page Renderer (v2).
 *
 * Base renderer for the homepage using the widgetized PL format through the v2 store-backed
 * renderer (`PLPageRendererV2`): every widget is read from the unified page widget store,
 * supporting nested widgets and lazy `fetchMode`. Layout pagination feeds the growing widget
 * list into the store via the shared `LayoutPaginationWrapper`.
 *
 * Mirrors the PDP/CLP PL renderers: wraps the feature provider, handles page hooks for state
 * capture/restoration, and passes tracking metadata for product card analytics.
 */

import { usePLPageStateCapture } from '@src/components/pl-base-widget/hooks/use-pl-page-state-capture'
import { usePLScrollRestoration } from '@src/components/pl-base-widget/hooks/use-pl-scroll-restoration'
import { PLPageMeta, PLPageProvider } from '@src/components/pl-page-context'
import { LayoutPaginationWrapper } from '@src/components/pl-page-renderer/layout-pagination-wrapper'
import { PLPageRendererV2 } from '@src/components/pl-page-renderer/pl-page-renderer-v2'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import { PLAPIResponse } from '@src/server-side/api/fetch-pl-data'
import { HomepageServerSideFeatureFlags } from '@src/server-side/server-feature-flags/feature-flag.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'

import { getWidgetRegistry } from './home-widget-registry'

export interface HomePagePLRendererProps {
  plData: PLAPIResponse
  pageMeta: PLPageMeta
  serverSideFeatureFlags?: HomepageServerSideFeatureFlags | null
}

// Homepage product card tracking context
const HOMEPAGE_PRODUCT_CARD_TRACKING_METADATA: TrackingMetaData = {
  context: {
    contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY,
    trackingId: 'home-product-display',
  },
}

/**
 * Internal component for page-scoped hooks (state capture, scroll restoration).
 * Must be rendered inside PLPageProvider context.
 */
function HomePageHooks() {
  // Capture page state (widget states + layout pagination state) before navigation
  usePLPageStateCapture()

  // Restore scroll position after widgets render
  usePLScrollRestoration()

  return null
}

/**
 * Internal content component rendered inside HomepageFeatureProvider.
 * Wraps the widgets to ensure they read HomepageFeatureContext.
 */
function HomePagePLRendererContent({ plData, pageMeta }: Readonly<HomePagePLRendererProps>) {
  const widgetRegistry = getWidgetRegistry()

  // Product-card impression analytics (mirrors PDP/CLP PL renderers). Product cards
  // read `productViewTracker` from page context and push per-card view events into the
  // batched stream; this hook initializes + flushes that stream.
  const trackerSlug = plData.meta?.slug ?? pageMeta.pathname
  const productViewTracker = `${trackerSlug}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}`

  const pageMetaWithTracking: PLPageMeta = {
    ...pageMeta,
    productViewTracker,
  }

  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData: HOMEPAGE_PRODUCT_CARD_TRACKING_METADATA,
  })

  return (
    <PLPageProvider key={pageMeta.pathname} pageMeta={pageMetaWithTracking}>
      <HomePageHooks />
      <LayoutPaginationWrapper
        initialWidgets={plData.widgets}
        initialPagination={plData.pagination}
        widgetRegistry={widgetRegistry}
        page={pageMeta.seoData?.title ?? ''}
        renderer={PLPageRendererV2}
      />
    </PLPageProvider>
  )
}

/**
 * Base renderer for homepage PL format.
 * Wraps the content with HomepageFeatureProvider to ensure widgets have access to
 * feature context (mirrors the CLP pattern with CollectionFeatureProvider).
 */
export function HomePagePLRenderer(props: Readonly<HomePagePLRendererProps>) {
  return <HomePagePLRendererContent {...props} />
}
